import { sendMfaCode, verifyMfaCode } from '@iheartjane/data-access';
import { MfaProvider } from '@iheartjane/menu';
import {
  Box,
  Flex,
  Loading,
  ReeferThemeProvider,
  Typography,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';

import { useAuthentication } from '@jane/shared-ecomm/providers';
import { trackOpenedPayByLink } from '@jane/shared-ecomm/tracking';
import { getStoreTheme } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { PageTitle } from '@jane/shared/components';
import type { LinkSource } from '@jane/shared/data-access';
import { useGetStore, useJaneUser } from '@jane/shared/data-access';
import { useLocalStorage } from '@jane/shared/hooks';

import { JaneAuthentication } from '../authentication/janeAuthentication';
import { PageLayout } from '../pageLayout/pageLayout';
import { BankLinkingPaymentFlow } from './BankLinkingPaymentFlow';
import { ErrorScreen } from './ErrorScreen';

const LINK_SOURCE_STORAGE_KEY = 'pay-by-link-source';

export const PostCheckoutPayment = () => {
  const hasTracked = useRef(false);
  const { cart_id = '', store_id = '' } = useParams<{
    cart_id?: string;
    store_id?: string;
  }>();
  const [queryParams] = useSearchParams();
  const querySource = queryParams.get('source') as LinkSource | null;
  // The source param is stripped from the URL once the page loads, so persist it
  const [storedSource, setStoredSource] = useLocalStorage<LinkSource | null>(
    LINK_SOURCE_STORAGE_KEY,
    null
  );
  const source = (querySource ?? storedSource) as LinkSource;

  useEffect(() => {
    if (querySource && querySource !== storedSource) {
      setStoredSource(querySource);
    }
  }, [querySource, storedSource]);

  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      trackOpenedPayByLink(source);
    }
  });

  const {
    errors,
    defaultLogin,
    logOut,
    onAppleClick,
    onGoogleClick,
    register,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuthentication();

  const {
    data: store,
    isPending: isLoadingStore,
    isError: isStoreError,
  } = useGetStore(store_id);

  const jwt = getJwt();
  const { data: janeUser, isPending: isLoadingUser } = useJaneUser();
  const authenticated = janeUser?.user?.authenticated;

  const isTablet = useTabletMediaQuery({});

  return (
    <ReeferThemeProvider theme={getStoreTheme(store)}>
      <MfaProvider
        getJwt={() => getJwt()}
        sendMfaCode={sendMfaCode}
        verifyMfaCode={verifyMfaCode}
        userId={janeUser?.user?.id ?? null}
      >
        <PageLayout>
          <PageTitle title="Pay via ACH" noIndex noFollow noSuffix />
          <Flex width="100%" height="100%" justifyContent="center">
            <Flex
              width={isTablet ? '50%' : '100%'}
              flexDirection="column"
              pt={32}
              px={32}
              mb={64}
            >
              {isLoadingUser || isLoadingStore ? (
                <Box py={48} position="relative">
                  <Loading color="black" size="lg" />
                </Box>
              ) : isStoreError ? (
                <Box p={24}>
                  <ErrorScreen error={'Error loading store'} />
                </Box>
              ) : authenticated && jwt ? (
                <BankLinkingPaymentFlow
                  cartID={cart_id}
                  store={store}
                  janeUser={janeUser}
                  linkSource={source}
                />
              ) : (
                <JaneAuthentication
                  errors={errors}
                  logOut={logOut}
                  onSignInSubmit={(data) =>
                    defaultLogin(data, 'post-checkout-payment')
                  }
                  onAppleClick={() => onAppleClick('post-checkout-payment')}
                  onCreateAccountSubmit={(data) => register(data)}
                  onGoogleClick={() => onGoogleClick('post-checkout-payment')}
                  onForgotPasswordSubmit={resetPassword}
                  showAppleLogin={showAppleLogin}
                  showGoogleLogin={showGoogleLogin}
                  signInBanner={
                    store && (
                      <Flex mb={20} flexDirection="column" alignItems="center">
                        <Typography mb={4} variant="h1-semibold">
                          Sign in to pay
                        </Typography>
                        <Typography variant="body-semibold">
                          at {store.name}
                        </Typography>
                      </Flex>
                    )
                  }
                  signInFooter={
                    <Typography textAlign="center" color="grays-mid">
                      We keep personal information safe and secure.
                    </Typography>
                  }
                />
              )}
            </Flex>
          </Flex>
        </PageLayout>
      </MfaProvider>
    </ReeferThemeProvider>
  );
};
